import Button from 'components/Button';
import IconColor from 'components/IconColor';
import Information from 'components/Information';
import Spinner from 'components/Loaders/Spinner';
import Modal from 'components/Modal';
import PDFReader from 'components/PdfReader';
import TooltipWrapper from 'components/Tooltip';
import {FC, useState} from 'react';
import {twMerge} from 'tailwind-merge';
import {c, jt, t} from 'ttag';
import {FileInfo, PDFFileInfo} from 'types/file-info';
import {PermissionResponse} from 'types/permissions';

import {useSupersendItemsFileInfo} from './services';

const isPdfFile = (file: FileInfo | PDFFileInfo): file is PDFFileInfo => {
    return file.fileMimetype === 'application/pdf';
};

type PdfItemProps = {
    label: string;
    onClick: () => void;
    permissions: {
        canView: PermissionResponse;
    };
    selected?: boolean;
};
const PdfItem: FC<PdfItemProps> = ({label, onClick, permissions, selected}) => {
    const disabled = !permissions.canView.allowed;

    return (
        <TooltipWrapper message={permissions.canView.failureReason}>
            <Button
                aria-label={label}
                aria-selected={selected}
                className={twMerge(
                    'text-wrap',
                    'flex gap-1 h-fit py-2',
                    'border-none rounded-none',
                    selected
                        ? 'bg-green-100 hover:bg-green-50 text-black'
                        : 'bg-white hover:bg-grey-100 text-black',
                    disabled && 'cursor-not-allowed opacity-50',
                )}
                disabled={disabled}
                kind="custom"
                onClick={disabled ? undefined : onClick}
            >
                <IconColor className="mt-1" icon="documents" size="base" />
                <span className="wrap-overflow-custom text-left">{label}</span>
            </Button>
        </TooltipWrapper>
    );
};

type PdfSelectorProps = {
    currentSelection: null | number;
    items: (FileInfo | PDFFileInfo)[];
    numPages: number;
    onSelect: (file: FileInfo | PDFFileInfo) => void;
    recipientContactOrganizationName: string;
};

const PdfSelector: FC<PdfSelectorProps> = ({
    currentSelection,
    items,
    numPages,
    onSelect,
    recipientContactOrganizationName: _recipientContactOrganizationName,
}) => {
    const recipientContactOrganizationName = (
        <span className="font-semibold" key="organization-name">
            {_recipientContactOrganizationName}
        </span>
    );
    const fileCount = items.length;

    return (
        <div
            className="flex min-w-80 max-w-xs flex-col gap-2"
            id="supersend-selector"
        >
            <div className="px-4 pt-2">
                <div className="wrap-overflow-custom">{jt`Sending Order for: ${recipientContactOrganizationName}`}</div>
                <div>{jt`File count: ${fileCount}`}</div>
                <div>{jt`Page count: ${numPages}`}</div>
            </div>
            <div className="flex flex-1 flex-col border border-grey-300 bg-grey-200">
                {items.map((file, index) => (
                    <PdfItem
                        key={file.fileUrl}
                        label={file.fileName}
                        onClick={() => onSelect(file)}
                        permissions={{
                            canView: file.permissions.canViewPdf || {
                                allowed: false,
                                failureReason: '',
                            },
                        }}
                        selected={currentSelection === index}
                    />
                ))}
            </div>
        </div>
    );
};

const MainContent: FC<{
    files: (FileInfo | PDFFileInfo)[];
    pageCount: number;
    recipientContactOrganizationName: string;
}> = ({files, pageCount, recipientContactOrganizationName}) => {
    const [pdfIndex, setPdfIndex] = useState<null | number>(() => {
        const initialIndex = files.findIndex(
            (file) => file.permissions.canViewPdf?.allowed,
        );
        return initialIndex !== -1 ? initialIndex : null;
    });
    const [selectedFile, setSelectedFile] = useState<
        FileInfo | null | PDFFileInfo
    >(() => {
        if (pdfIndex === null) return null;
        const initialFile = files[pdfIndex];
        return isPdfFile(initialFile) ? initialFile : null;
    });

    return (
        <Modal.Body className="flex h-screen max-h-[80vh] p-0">
            <PdfSelector
                currentSelection={pdfIndex}
                items={files}
                numPages={pageCount}
                onSelect={(newFile) => {
                    if (!newFile.permissions.canViewPdf?.allowed) return;
                    setSelectedFile(newFile);
                    setPdfIndex(files.indexOf(newFile));
                }}
                recipientContactOrganizationName={
                    recipientContactOrganizationName
                }
            />
            {(!selectedFile || !isPdfFile(selectedFile)) && (
                <div className="flex size-full flex-col bg-grey-200 p-4 text-grey-600">
                    {c('component').t`No file chosen`}
                </div>
            )}
            {selectedFile && isPdfFile(selectedFile) && (
                <PDFReader
                    key={selectedFile.pdfPreviewUrl}
                    maxPages={selectedFile.maxPages}
                    showControls={true}
                    src={selectedFile.pdfPreviewUrl}
                />
            )}
        </Modal.Body>
    );
};

type ModalPdfReaderProps = {
    isOpen: boolean;
    onClose: () => void;
    partnerMid: string;
    supersendMid: string;
};

/**
 * This is a reusable component that is used to display all the supersend items in a supersend.
 */
const SupersendItemsPreviewModal: FC<ModalPdfReaderProps> = ({
    isOpen,
    onClose,
    partnerMid,
    supersendMid,
}) => {
    const {data, isLoading} = useSupersendItemsFileInfo(
        partnerMid,
        supersendMid,
    );
    if (!isOpen) return null;

    let mainContent: JSX.Element;

    if (isLoading) {
        mainContent = (
            <Modal.Body className="flex justify-center py-0">
                <Spinner />
            </Modal.Body>
        );
    } else if (data) {
        mainContent = (
            <MainContent
                files={data.items}
                pageCount={data.pageCount}
                recipientContactOrganizationName={
                    data.recipientContactOrganizationName
                }
            />
        );
    } else {
        mainContent = (
            <Modal.Body className="py-0">
                <Information type="alert">
                    {t`An unexpected error occurred.`}
                </Information>
            </Modal.Body>
        );
    }

    return (
        <Modal
            className="w-full max-w-[80vw]"
            isOpen={isOpen}
            onClose={onClose}
            preventCloseOnEnterPress={true}
        >
            <Modal.Header onClose={onClose}>{t`Preview Files`}</Modal.Header>
            {mainContent}
        </Modal>
    );
};

export default SupersendItemsPreviewModal;
